import React, { useEffect, useMemo, useState } from "react";
import { updateLastActivity, clearSession } from "../services/authService";
import Navbar from "../components/NavBar";
import { Editor } from "primereact/editor";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Dropdown } from "primereact/dropdown";
import { Button } from "primereact/button";
import { Tag } from "primereact/tag";
import { Dialog } from "primereact/dialog";
import { Divider } from "primereact/divider";

const categoryOptions = [
  { label: "Noticias", value: "Noticias" },
  { label: "Eventos", value: "Eventos" },
  { label: "Académico", value: "Académico" },
  { label: "Marketing", value: "Marketing" },
  { label: "Tecnología", value: "Tecnología" },
  { label: "General", value: "General" },
];

const statusOptions = [
  { label: "Borrador", value: "Borrador" },
  { label: "Pendiente de revisión", value: "Pendiente de revisión" },
  { label: "Publicado", value: "Publicado" },
];

const generateSlug = (text) => {
  if (!text) return "";
  return String(text)
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");
};

const countWords = (text) => {
  if (!text || !text.trim()) return 0;
  return text.trim().split(/\s+/).length;
};

export default function BlogEntrada() {
  const [title, setTitle] = useState("");
  const [slug, setSlug] = useState("");
  const [author, setAuthor] = useState("");
  const [category, setCategory] = useState(null);
  const [featuredImage, setFeaturedImage] = useState("");
  const [excerpt, setExcerpt] = useState("");
  const [content, setContent] = useState("");
  const [tagsInput, setTagsInput] = useState("");
  const [status, setStatus] = useState("Borrador");

  const [showPreview, setShowPreview] = useState(false);
  const [successDialog, setSuccessDialog] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    updateLastActivity();

    const timer = setInterval(() => {
      const now = Date.now();
      const last = parseInt(localStorage.getItem("lastActivity") || "0", 10);

      if (now - last > 15 * 60 * 1000) {
        clearSession();
        window.location.href = "/login";
      }
    }, 60 * 1000);

    const update = () => updateLastActivity();

    window.addEventListener("mousemove", update);
    window.addEventListener("keydown", update);
    window.addEventListener("beforeunload", clearSession);

    return () => {
      clearInterval(timer);
      window.removeEventListener("mousemove", update);
      window.removeEventListener("keydown", update);
      window.removeEventListener("beforeunload", clearSession);
    };
  }, []);

  useEffect(() => {
    if (title && !slug) {
      setSlug(generateSlug(title));
    }
  }, [title, slug]);

  const tags = useMemo(() => {
    return tagsInput
      .split(",")
      .map((tag) => tag.trim())
      .filter(Boolean);
  }, [tagsInput]);

  const wordCount = useMemo(() => countWords(content), [content]);

  const handleTitleChange = (e) => {
    const value = e.target.value;
    setTitle(value);
    setSlug(generateSlug(value));
  };

  const handleSaveDraft = () => {
    setErrorMessage("");

    if (!title.trim()) {
      setErrorMessage("Debes ingresar al menos el título de la entrada.");
      return;
    }

    const payload = {
      title,
      slug,
      author,
      category,
      featuredImage,
      excerpt,
      content,
      tags,
      status: "Borrador",
      createdAt: new Date().toISOString(),
    };

    console.log("Borrador guardado:", payload);
    setStatus("Borrador");
    setSuccessDialog(true);
  };

  const handlePublish = () => {
    setErrorMessage("");

    if (!title.trim()) {
      setErrorMessage("El título es obligatorio.");
      return;
    }

    if (!author.trim()) {
      setErrorMessage("El autor es obligatorio.");
      return;
    }

    if (!category) {
      setErrorMessage("Debes seleccionar una categoría.");
      return;
    }

    if (!excerpt.trim()) {
      setErrorMessage("Debes escribir un extracto.");
      return;
    }

    if (!content.trim()) {
      setErrorMessage("Debes escribir el contenido de la entrada.");
      return;
    }

    const payload = {
      title,
      slug,
      author,
      category,
      featuredImage,
      excerpt,
      content,
      tags,
      status: "Publicado",
      publishedAt: new Date().toISOString(),
    };

    console.log("Entrada publicada:", payload);
    setStatus("Publicado");
    setSuccessDialog(true);
  };

  const handleClear = () => {
    setTitle("");
    setSlug("");
    setAuthor("");
    setCategory(null);
    setFeaturedImage("");
    setExcerpt("");
    setContent("");
    setTagsInput("");
    setStatus("Borrador");
    setErrorMessage("");
  };

  return (
    <div style={{ minHeight: "100vh", background: "#f5f5f5" }}>
      <Navbar />

      <main style={{ padding: "2rem" }}>
        <h1 style={{ fontWeight: 800, color: "#0f3b63", marginBottom: "1rem" }}>
          Herramienta para crear una entrada de blog
        </h1>

        <p style={{ maxWidth: 760, lineHeight: 1.6, marginBottom: "1.5rem" }}>
          Aquí puedes redactar, organizar y preparar una nueva publicación para
          el blog. Completa la información general, agrega el contenido de la
          entrada y revisa una vista previa antes de guardarla o publicarla.
        </p>

        <section className="grid" style={{ margin: 0, alignItems: "start" }}>
          <div className="col-12 lg:col-8" style={{ paddingLeft: 0 }}>
            <div
              style={{
                background: "#ffffff",
                padding: "1.5rem",
                borderRadius: 12,
                boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
                marginBottom: "1.5rem",
              }}
            >
              <h2
                style={{
                  marginTop: 0,
                  marginBottom: "1rem",
                  color: "#0f3b63",
                  fontSize: "1.2rem",
                }}
              >
                Información general
              </h2>

              <div className="grid">
                <div className="col-12">
                  <label
                    style={{
                      display: "block",
                      marginBottom: "0.5rem",
                      fontWeight: 600,
                      color: "#0f3b63",
                    }}
                  >
                    Título de la entrada
                  </label>
                  <InputText
                    value={title}
                    onChange={handleTitleChange}
                    placeholder="Escribe el título del blog"
                    style={{ width: "100%" }}
                  />
                </div>

                <div className="col-12 md:col-6">
                  <label
                    style={{
                      display: "block",
                      marginBottom: "0.5rem",
                      fontWeight: 600,
                      color: "#0f3b63",
                    }}
                  >
                    Slug
                  </label>
                  <InputText
                    value={slug}
                    onChange={(e) => setSlug(generateSlug(e.target.value))}
                    placeholder="slug-de-la-entrada"
                    style={{ width: "100%" }}
                  />
                </div>

                <div className="col-12 md:col-6">
                  <label
                    style={{
                      display: "block",
                      marginBottom: "0.5rem",
                      fontWeight: 600,
                      color: "#0f3b63",
                    }}
                  >
                    Autor
                  </label>
                  <InputText
                    value={author}
                    onChange={(e) => setAuthor(e.target.value)}
                    placeholder="Nombre del autor"
                    style={{ width: "100%" }}
                  />
                </div>

                <div className="col-12 md:col-6">
                  <label
                    style={{
                      display: "block",
                      marginBottom: "0.5rem",
                      fontWeight: 600,
                      color: "#0f3b63",
                    }}
                  >
                    Categoría
                  </label>
                  <Dropdown
                    value={category}
                    options={categoryOptions}
                    onChange={(e) => setCategory(e.value)}
                    placeholder="Selecciona una categoría"
                    style={{ width: "100%" }}
                  />
                </div>

                <div className="col-12 md:col-6">
                  <label
                    style={{
                      display: "block",
                      marginBottom: "0.5rem",
                      fontWeight: 600,
                      color: "#0f3b63",
                    }}
                  >
                    Estado
                  </label>
                  <Dropdown
                    value={status}
                    options={statusOptions}
                    onChange={(e) => setStatus(e.value)}
                    placeholder="Selecciona un estado"
                    style={{ width: "100%" }}
                  />
                </div>

                {/*<div className="col-12">
                  <label
                    style={{
                      display: "block",
                      marginBottom: "0.5rem",
                      fontWeight: 600,
                      color: "#0f3b63",
                    }}
                  >
                    URL de imagen principal
                  </label>
                  <InputText
                    value={featuredImage}
                    onChange={(e) => setFeaturedImage(e.target.value)}
                    placeholder="https://..."
                    style={{ width: "100%" }}
                  />
                </div>*/}

                <div className="col-12">
                  <label
                    style={{
                      display: "block",
                      marginBottom: "0.5rem",
                      fontWeight: 600,
                      color: "#0f3b63",
                    }}
                  >
                    Extracto
                  </label>
                  <InputTextarea
                    value={excerpt}
                    onChange={(e) => setExcerpt(e.target.value)}
                    rows={4}
                    placeholder="Escribe un resumen corto de la entrada"
                    style={{ width: "100%", resize: "vertical" }}
                  />
                </div>
              </div>
            </div>

            <div
              style={{
                background: "#ffffff",
                padding: "1.5rem",
                borderRadius: 12,
                boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
                marginBottom: "1.5rem",
              }}
            >
              <h2
                style={{
                  marginTop: 0,
                  marginBottom: "1rem",
                  color: "#0f3b63",
                  fontSize: "1.2rem",
                }}
              >
                Contenido de la entrada
              </h2>

              <div className="grid">
                <div className="col-12">
                  <label
                    style={{
                      display: "block",
                      marginBottom: "0.5rem",
                      fontWeight: 600,
                      color: "#0f3b63",
                    }}
                  >
                    Contenido principal
                  </label>
                  <Editor
                    value={content}
                    onTextChange={(e) => setContent(e.htmlValue)}
                    style={{ height: "320px" }}
                    placeholder="Escribe aquí el contenido completo de la entrada..."
                  />
                </div>

                <div className="col-12">
                  <label
                    style={{
                      display: "block",
                      marginBottom: "0.5rem",
                      fontWeight: 600,
                      color: "#0f3b63",
                    }}
                  >
                    Etiquetas
                  </label>
                  <InputText
                    value={tagsInput}
                    onChange={(e) => setTagsInput(e.target.value)}
                    placeholder="Ejemplo: marketing, blog, noticias"
                    style={{ width: "100%" }}
                  />
                  <small
                    style={{
                      display: "block",
                      marginTop: "0.5rem",
                      color: "#666",
                    }}
                  >
                    Separa las etiquetas con comas.
                  </small>
                </div>
              </div>
            </div>

            {errorMessage && (
              <div
                style={{
                  background: "#fdeaea",
                  color: "#b00020",
                  padding: "1rem",
                  borderRadius: 10,
                  marginBottom: "1rem",
                  border: "1px solid #f3c2c7",
                }}
              >
                {errorMessage}
              </div>
            )}

            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: "0.75rem",
                marginBottom: "2rem",
              }}
            >
              <Button
                label="Guardar borrador"
                icon="pi pi-save"
                onClick={handleSaveDraft}
                style={{
                  background: "#0f3b63",
                  border: "none",
                  borderRadius: 8,
                }}
              />

              <Button
                label="Vista previa"
                icon="pi pi-eye"
                severity="secondary"
                onClick={() => setShowPreview(true)}
                style={{ borderRadius: 8 }}
              />

              <Button
                label="Publicar"
                icon="pi pi-send"
                onClick={handlePublish}
                style={{
                  background: "#2e7d32",
                  border: "none",
                  borderRadius: 8,
                }}
              />

              <Button
                label="Limpiar"
                icon="pi pi-times"
                severity="danger"
                outlined
                onClick={handleClear}
                style={{ borderRadius: 8 }}
              />
            </div>
          </div>

          <div className="col-12 lg:col-4" style={{ paddingRight: 0 }}>
            <div
              style={{
                background: "#ffffff",
                padding: "1.5rem",
                borderRadius: 12,
                boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
                marginBottom: "1.5rem",
              }}
            >
              <h2
                style={{
                  marginTop: 0,
                  marginBottom: "1rem",
                  color: "#0f3b63",
                  fontSize: "1.2rem",
                }}
              >
                Resumen rápido
              </h2>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr",
                  gap: "0.75rem",
                }}
              >
                <div
                  style={{
                    background: "#f8fafc",
                    padding: "1rem",
                    borderRadius: 10,
                  }}
                >
                  <p style={{ margin: 0, fontSize: "0.85rem", color: "#555" }}>
                    Título
                  </p>
                  <p style={{ margin: 0, fontWeight: 700, color: "#0f3b63" }}>
                    {title || "Sin definir"}
                  </p>
                </div>

                <div
                  style={{
                    background: "#f8fafc",
                    padding: "1rem",
                    borderRadius: 10,
                  }}
                >
                  <p style={{ margin: 0, fontSize: "0.85rem", color: "#555" }}>
                    Estado
                  </p>
                  <div style={{ marginTop: "0.35rem" }}>
                    <Tag value={status || "Sin estado"} severity="info" />
                  </div>
                </div>

                <div
                  style={{
                    background: "#f8fafc",
                    padding: "1rem",
                    borderRadius: 10,
                  }}
                >
                  <p style={{ margin: 0, fontSize: "0.85rem", color: "#555" }}>
                    Palabras
                  </p>
                  <p style={{ margin: 0, fontWeight: 700, color: "#0f3b63" }}>
                    {wordCount}
                  </p>
                </div>

                <div
                  style={{
                    background: "#f8fafc",
                    padding: "1rem",
                    borderRadius: 10,
                  }}
                >
                  <p style={{ margin: 0, fontSize: "0.85rem", color: "#555" }}>
                    Categoría
                  </p>
                  <p style={{ margin: 0, fontWeight: 700, color: "#0f3b63" }}>
                    {category || "Sin categoría"}
                  </p>
                </div>

                <div
                  style={{
                    background: "#f8fafc",
                    padding: "1rem",
                    borderRadius: 10,
                  }}
                >
                  <p style={{ margin: 0, fontSize: "0.85rem", color: "#555" }}>
                    Etiquetas
                  </p>
                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: "0.5rem",
                      marginTop: "0.5rem",
                    }}
                  >
                    {tags.length > 0 ? (
                      tags.map((tag, index) => (
                        <Tag key={`${tag}-${index}`} value={tag} />
                      ))
                    ) : (
                      <span style={{ color: "#777", fontSize: "0.9rem" }}>
                        Sin etiquetas
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div
              style={{
                background: "#ffffff",
                padding: "1.5rem",
                borderRadius: 12,
                boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
              }}
            >
              <h2
                style={{
                  marginTop: 0,
                  marginBottom: "1rem",
                  color: "#0f3b63",
                  fontSize: "1.2rem",
                }}
              >
                Recomendaciones
              </h2>

              <ul style={{ paddingLeft: "1.2rem", margin: 0, lineHeight: 1.7 }}>
                <li>Usa un título claro y directo.</li>
                <li>Escribe un extracto breve que resuma la publicación.</li>
                <li>Divide el contenido en párrafos fáciles de leer.</li>
                <li>Agrega etiquetas para organizar mejor el blog.</li>
                <li>Verifica la vista previa antes de publicar.</li>
              </ul>
            </div>
          </div>
        </section>

        <Dialog
          header="Vista previa de la entrada"
          visible={showPreview}
          style={{ width: "80vw", maxWidth: 900 }}
          modal
          onHide={() => setShowPreview(false)}
        >
          <div>
            {featuredImage ? (
              <img
                src={featuredImage}
                alt="Imagen principal"
                style={{
                  width: "100%",
                  maxHeight: 320,
                  objectFit: "cover",
                  borderRadius: 10,
                  marginBottom: "1rem",
                }}
              />
            ) : null}

            <h1 style={{ color: "#0f3b63", marginBottom: "0.5rem" }}>
              {title || "Título de la entrada"}
            </h1>

            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: "0.75rem",
                alignItems: "center",
                marginBottom: "1rem",
              }}
            >
              {author ? (
                <Tag value={`Autor: ${author}`} severity="info" />
              ) : null}
              {category ? <Tag value={category} severity="success" /> : null}
              {status ? <Tag value={status} severity="warning" /> : null}
            </div>

            <p
              style={{
                fontSize: "1rem",
                lineHeight: 1.7,
                color: "#555",
                fontStyle: "italic",
              }}
            >
              {excerpt || "Aquí aparecerá el extracto de la entrada."}
            </p>

            <Divider />

            <div
              style={{
                lineHeight: 1.8,
                color: "#222",
              }}
              dangerouslySetInnerHTML={{
                __html:
                  content ||
                  "<p>Aquí aparecerá el contenido principal de la entrada.</p>",
              }}
            />
            {tags.length > 0 && (
              <>
                <Divider />
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "0.5rem",
                  }}
                >
                  {tags.map((tag, index) => (
                    <Tag key={`preview-${tag}-${index}`} value={tag} />
                  ))}
                </div>
              </>
            )}
          </div>
        </Dialog>

        <Dialog
          header="Operación realizada"
          visible={successDialog}
          style={{ width: "30vw", minWidth: 320 }}
          modal
          onHide={() => setSuccessDialog(false)}
        >
          <p style={{ margin: 0, lineHeight: 1.6 }}>
            La entrada del blog fue procesada correctamente con estado:{" "}
            <strong>{status}</strong>.
          </p>
        </Dialog>
      </main>
    </div>
  );
}
